import React, { useContext, useEffect, useLayoutEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import ApiDevice from "../../../app/api/admin/device/ApiDevice";
import { screenshotsUrl } from "./index";
import Page from "../../modules/page";
import { ContextContent } from "../../modules/content/contentContext";
import Loader from "../../components/loader";
import DateLib from "../../../inc/DateLib";
import "./styles.css";

/**
 * Карточка устройства.
 *
 * Появилась под ссылку из ЛК аккаунтов: оттуда приходят по имени агента, чтобы посмотреть, на
 * чём он живёт и какой аккаунт на нём стоит. Раздел «Устройства» до этого был только списком.
 *
 * Read-only: правки (имя, тариф, аккаунт) живут в списке, где они делаются пачкой и рядом с
 * остальными устройствами. Дублировать их здесь значит держать две формы для одного и того же.
 */
export default function DeviceShow({ ...other }) {
    return <Page ApiModel={ApiDevice} {...other}>
        <DeviceShowContent/>
    </Page>;
}

function Field({ label, children, mono = false }) {
    return <div className="device-show__field">
        <div className="device-show__label">{label}</div>
        <div className={`device-show__value ${mono ? 'device-show__value_mono' : ''}`}>{children ?? '—'}</div>
    </div>;
}

function DeviceShowContent() {
    const contextContent = useContext(ContextContent);
    const deviceTypes = useSelector((state) => state.option.deviceTypes);
    const deviceHealthStatuses = useSelector((state) => state.option.deviceHealthStatuses);

    const [_device, set_device] = useState(null);
    const [_account, set_account] = useState(null);

    useLayoutEffect(() => {
        contextContent._handleSetData.current = (data) => {
            set_device(data ?? null);
            contextContent.setTitle(`Устройство: ${data?.name ?? data?.id ?? ''}`);
        };
        // Провайдер сам загрузку НЕ запускает — doLoad он только отдаёт в контекст, и зовёт его
        // страница (так же во всех остальных карточках: nodes, users, files, roles).
        contextContent.doLoad();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Аккаунт резолвим тем же путём, что и список: устройство хранит только его id, а логин и
    // ссылка на карточку живут в сервисе аккаунтов.
    useEffect(() => {
        const accountId = _device?.external_id;
        if (!accountId) {
            set_account(null);
            return;
        }
        let isActual = true;
        ApiDevice.Accounts().then((rows) => {
            if (!isActual) return;
            set_account((rows ?? []).find((a) => String(a.id) === String(accountId)) ?? null);
        });
        return () => { isActual = false; };
    }, [_device?.external_id]);

    if (!_device) return <Loader/>;

    const typeCode = _device.device_type?.code
        ?? _device.device_type_code
        ?? deviceTypes?.[_device.device_type_id]
        ?? null;

    return <div className="device-show">
        <div className="device-show__grid">
            <Field label="ID" mono>#{_device.id}</Field>
            <Field label="Имя">{_device.name}</Field>
            <Field label="Статус">{_device.status}</Field>
            {/* По последнему аудиту ноды; null — аудит ещё не проверял. */}
            <Field label="Здоровье">
                {_device.health_status ? (deviceHealthStatuses[_device.health_status] ?? _device.health_status) : null}
            </Field>
            <Field label="Нода">{_device.node?.name}</Field>
            <Field label="Тариф" mono>{typeCode}</Field>
            <Field label="Аккаунт">
                {account_link(_account, _device.external_id)}
            </Field>
            <Field label="Контейнер" mono>{_device.container_name}</Field>
            <Field label="adb-порт" mono>{_device.adb_port}</Field>
            <Field label="Часовой пояс">{_device.timezone}</Field>
            <Field label="Провижн" mono>{_device.provision_version}</Field>
            <Field label="Заведено">
                {_device.created_at ? DateLib.formatterDateTime(new Date(_device.created_at)) : null}
            </Field>
            <Field label="Заметка">{_device.comment}</Field>
        </div>

        {_device.screenshot_url ? <div className="device-show__screen">
            <div className="device-show__label">
                Снимок экрана
                {_device.screenshot_at
                    ? ` — ${DateLib.formatterDateTime(new Date(_device.screenshot_at))}`
                    : ''}
            </div>
            {/* Файл лежит по постоянному пути и перезаписывается на месте, поэтому кеш бьём
                датой снимка — иначе браузер показывал бы вчерашний кадр. */}
            <img
                className="device-show__screen-img"
                src={`${_device.screenshot_url}?v=${encodeURIComponent(_device.screenshot_at ?? '')}`}
                alt={`Экран устройства ${_device.name ?? _device.id}`}
            />
            {/* В карточке живёт только ПОСЛЕДНИЙ кадр (devices.screenshot_file_id), история
                предыдущих — в разделе «Скриншоты», туда и уводим с готовым фильтром. */}
            <Link className="device-show__screen-link" to={screenshotsUrl(_device.id)}>
                Все снимки устройства →
            </Link>
        </div> : null}
    </div>;
}

function account_link(account, externalId) {
    if (!externalId) return null;
    if (!account) return `#${externalId}`;
    return <a href={account.url} target="_blank" rel="noopener noreferrer">{account.login}</a>;
}
